import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { InstagramAccount } from '@frontend/gql/social/graphql';
import {
  ContentPreviewData,
  ProjectEditingState,
  GroupEditingState,
  ContentPreviewSheetProps,
  MetaAdAccount,
} from '../types';
import { BoostPostFormValues } from '../types/boost-post-schema';

interface UseContentPreviewOptions<T> {
  /**
   * The URL parameter name to use for storing the content ID
   */
  urlParamName?: string;

  /**
   * Function to adapt source data to ContentPreviewData format
   */
  adapter?: (data: T) => ContentPreviewData;

  /**
   * Initial data items to search from when using URL parameters
   */
  items?: T[];

  /**
   * Function to get a unique ID from a content item
   */
  getItemId: (item: T) => string;

  /**
   * Optional project editing configuration
   */
  projectEditing?: {
    onSave?: (projectId: string, projectName: string) => Promise<void>;
    onCancel?: () => void;
  };

  /**
   * Optional group editing configuration
   */
  groupEditing?: {
    onSave?: (groupId: string, groupName: string) => Promise<void>;
    onCancel?: () => void;
  };

  /**
   * Optional available projects
   */
  availableProjects?: Array<{ id: string; name: string }>;

  /**
   * Optional available groups
   */
  availableGroups?: Array<{ id: string; name: string }>;

  /**
   * Optional available meta ad accounts
   */
  availableMetaAdAccounts?: MetaAdAccount[];

  /**
   * Optional available instagram accounts
   */
  availableInstagramAccounts?: InstagramAccount[];

  /**
   * Function to handle boost post
   */
  onBoostPost?: (
    boostPostFormValues: BoostPostFormValues,
    setFormValue?: (name: string, value: unknown) => void,
  ) => Promise<{ successMessage?: string }>;
}

interface UseContentPreviewReturn<T> {
  /**
   * The currently selected content
   */
  selectedContent: ContentPreviewData | null;

  /**
   * Function to set the selected content
   */
  setSelectedContent: (content: T | null) => void;

  /**
   * Whether the preview is open
   */
  isPreviewOpen: boolean;

  /**
   * Function to set whether the preview is open
   */
  setIsPreviewOpen: (open: boolean) => void;

  /**
   * Project editing state
   */
  projectEditingState: ProjectEditingState;

  /**
   * Function to update project editing state
   */
  updateProjectEditingState: (updates: Partial<ProjectEditingState>) => void;

  /**
   * Group editing state
   */
  groupEditingState: GroupEditingState;

  /**
   * Props to pass to the ContentPreviewSheet component
   */
  previewProps: ContentPreviewSheetProps | null;
}

/**
 * Custom hook for managing content preview state and URL synchronization
 */
export function useContentPreview<T>({
  urlParamName = 'contentId',
  adapter,
  items = [],
  getItemId,
  projectEditing,
  groupEditing,
  availableProjects,
  availableGroups,
  availableMetaAdAccounts,
  availableInstagramAccounts,
  onBoostPost,
}: UseContentPreviewOptions<T>): UseContentPreviewReturn<T> {
  const history = useHistory();
  const location = useLocation();
  const [isPreviewOpen, setIsPreviewOpenState] = useState(false);
  const [projectEditingState, setProjectEditingState] = useState<ProjectEditingState>({
    isEditing: false,
  });
  const [groupEditingState, setGroupEditingState] = useState<GroupEditingState>({
    isEditing: false,
  });

  // Use refs to track previous values and prevent infinite loops
  const prevLocationSearch = useRef(location.search);
  const isUpdatingUrl = useRef(false);
  const hasInitialized = useRef(false);

  // Get content ID from URL
  const getContentIdFromUrl = useCallback(() => {
    if (typeof window === 'undefined') return null;
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get(urlParamName);
  }, [location.search, urlParamName]);

  // Update URL without triggering effects
  const updateUrlSafely = useCallback(
    (contentId: string | null) => {
      if (typeof window === 'undefined' || isUpdatingUrl.current) return;

      isUpdatingUrl.current = true;
      try {
        const currentParams = new URLSearchParams(location.search);
        const hasParam = currentParams.has(urlParamName);

        // Only update URL if needed
        if (contentId && (!hasParam || currentParams.get(urlParamName) !== contentId)) {
          currentParams.set(urlParamName, contentId);
          history.replace({
            pathname: location.pathname,
            search: currentParams.toString(),
          });
        } else if (!contentId && hasParam) {
          currentParams.delete(urlParamName);
          history.replace({
            pathname: location.pathname,
            search: currentParams.toString(),
          });
        }
      } finally {
        isUpdatingUrl.current = false;
      }
    },
    [history, location.pathname, location.search, urlParamName],
  );

  // Derive selected content from URL and items
  const selectedContent = useMemo(() => {
    const contentId = getContentIdFromUrl();
    if (!contentId || !adapter || items.length === 0) return null;

    const item = items.find((item) => getItemId(item) === contentId);
    return item ? adapter(item) : null;
  }, [getContentIdFromUrl, adapter, items, getItemId]);

  // Initial load and URL sync effect
  useEffect(() => {
    // Skip if already initialized
    if (hasInitialized.current || items.length === 0) return;

    const contentId = getContentIdFromUrl();
    if (contentId) {
      const hasMatchingItem = items.some((item) => getItemId(item) === contentId);

      // If we have matching content, update preview state
      if (hasMatchingItem) {
        setIsPreviewOpenState(true);
        hasInitialized.current = true;
        // eslint-disable-next-line @typescript-eslint/brace-style
      }
      // If items are available but content not found, clear the URL
      else if (items.length > 0) {
        updateUrlSafely(null);
      }
    }

    hasInitialized.current = true;
  }, [items, getContentIdFromUrl, updateUrlSafely, getItemId]);

  // Handle URL changes after initial load
  useEffect(() => {
    // Skip during initialization or if URL hasn't changed
    if (!hasInitialized.current || prevLocationSearch.current === location.search || isUpdatingUrl.current) {
      prevLocationSearch.current = location.search;
      return;
    }

    const contentId = getContentIdFromUrl();
    setIsPreviewOpenState(!!contentId);
    prevLocationSearch.current = location.search;
  }, [location.search, getContentIdFromUrl]);

  const updateProjectEditingState = useCallback((updates: Partial<ProjectEditingState>) => {
    setProjectEditingState((prev) => ({
      ...prev,
      ...updates,
    }));
  }, []);

  const updateGroupEditingState = useCallback((updates: Partial<GroupEditingState>) => {
    setGroupEditingState((prev) => ({
      ...prev,
      ...updates,
    }));
  }, []);

  // Set selected content
  const setSelectedContent = useCallback(
    (content: T | null) => {
      const contentId = content ? getItemId(content) : null;
      updateUrlSafely(contentId);
      setIsPreviewOpenState(!!content);
    },
    [getItemId, updateUrlSafely],
  );

  // Handle open/close state
  const setIsPreviewOpen = useCallback(
    (open: boolean) => {
      setIsPreviewOpenState(open);

      // When closing, remove content ID from URL and reset editing states
      if (!open) {
        updateUrlSafely(null);
        setProjectEditingState({ isEditing: false });
        setGroupEditingState({ isEditing: false });
      } else if (open && selectedContent?.id) {
        updateUrlSafely(selectedContent.id);
      }
      // Mark as initialized since user is interacting with preview
      hasInitialized.current = true;
    },
    [selectedContent, updateUrlSafely],
  );

  // Prepare props for ContentPreviewSheet
  const previewProps = selectedContent
    ? {
        content: {
          ...selectedContent,
          onEditProject: () => {
            if (projectEditing) {
              setProjectEditingState({
                isEditing: true,
                projectId:
                  selectedContent.metadata.project?.id !== undefined
                    ? String(selectedContent.metadata.project.id)
                    : '__no_assignment__',
                projectName: selectedContent.metadata.project?.name || null,
                onSave: projectEditing.onSave,
                onCancel: () => {
                  setProjectEditingState({ isEditing: false });
                  projectEditing.onCancel?.();
                },
              });
            }
          },
          onEditGroup: () => {
            if (groupEditing) {
              setGroupEditingState({
                isEditing: true,
                groupId:
                  selectedContent.metadata.group?.id !== undefined
                    ? String(selectedContent.metadata.group.id)
                    : '__no_assignment__',
                groupName: selectedContent.metadata.group?.name || null,
                onSave: groupEditing.onSave,
                onCancel: () => {
                  setGroupEditingState({ isEditing: false });
                  groupEditing.onCancel?.();
                },
              });
            }
          },
          projectEditingState: {
            ...projectEditingState,
            onSave: projectEditing?.onSave,
            onCancel: () => {
              setProjectEditingState({ isEditing: false });
              projectEditing?.onCancel?.();
            },
            updateState: updateProjectEditingState,
          },
          groupEditingState: {
            ...groupEditingState,
            onSave: groupEditing?.onSave,
            onCancel: () => {
              setGroupEditingState({ isEditing: false });
              groupEditing?.onCancel?.();
            },
            updateState: updateGroupEditingState,
          },
        },
        open: isPreviewOpen,
        onOpenChange: setIsPreviewOpen,
        availableProjects: availableProjects || [],
        availableGroups: availableGroups || [],
        availableMetaAdAccounts: availableMetaAdAccounts || [],
        availableInstagramAccounts: availableInstagramAccounts || [],
        onBoostPost: onBoostPost || (() => Promise.resolve({ successMessage: undefined })),
      }
    : null;

  return {
    selectedContent,
    setSelectedContent,
    isPreviewOpen,
    setIsPreviewOpen,
    projectEditingState,
    updateProjectEditingState,
    groupEditingState,
    previewProps,
  };
}

export default useContentPreview;
